import * as React from 'react';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';

import { Select } from '@common/components/form/inputs/select';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import Container from '../../../../common/components/container';
import Overview from '../../../../common/components/overview';
import Bar from '../../../../common/components/bar';
import Spinner from '../../../../common/components/spinner';
import Placeholder from '../../../../common/components/placeholder';
import List from '../../../../common/components/list';
import UserCount from '../../components/user-count';
import DateRangeSelector, { getDates } from '../../components/date-range-selector';
import { EMetricTypes } from '../../../../common/definitions';
import * as organisationSelector from '../../../organisation/selectors/organisation';

const { Row } = Overview.Content;

const formatAverageTime = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  } if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  }

  return `${(seconds / 3600).toFixed(1)}h`;
};

class StatisticsFlexchangeContainer extends React.Component {
  constructor(props) {
    super();

    this.state = {
      isInitialising: !!props.analytics[EMetricTypes.EXCHANGES_CREATED],
      isFetching: true,
      selectedNetwork: props.match.params.networkId || false,
      selectedDateValue: 2,
      dates: getDates(2),
    };

    this.handleSetDateRange = this.handleSetDateRange.bind(this);
    this.handleChangeSelectedNetwork = (option) => this.setState({ selectedNetwork: option.id }, this.handleFetchData);
  }

  UNSAFE_componentWillMount() {
    this.handleFetchData();
  }

  static props;

  async handleFetchData() {
    const { dates: [current, comparison] } = this.state;

    this.setState({ isFetching: true });

    await Promise.all([
      this.props.fetchAnalytics(
        [EMetricTypes.EXCHANGES_CREATED, EMetricTypes.EXCHANGES_APPROVED, EMetricTypes.EXCHANGES_TOTAL_APPROVED_TIME_IN_SECONDS],
        current.start,
        current.end,
        comparison.start,
        comparison.end,
        this.state.selectedNetwork,
      ),
      this.props.fetchAnalyticsUsers(
        [EMetricTypes.EXCHANGES_CREATED, EMetricTypes.EXCHANGES_APPROVED],
        current.start,
        current.end,
        this.state.selectedNetwork,
      ),
    ]);

    this.setState({ isInitialising: false, isFetching: false });
  }

  handleSetDateRange({ value, dates }) {
    this.setState({
      selectedDateValue: value,
      dates,
    }, this.handleFetchData);
  }

  render() {
    const { isInitialising, isFetching, selectedNetwork, selectedDateValue } = this.state;
    const { match: { params }, analytics, networks, t } = this.props;

    const getTotal = (key) => (analytics.data[key] ? analytics.data[key].total : 0);
    const getOldTotal = (key) => (analytics.data[key] ? analytics.data[key].old_total : 0);
    const getUniqueUsers = (key) => (analytics.data[key] ? analytics.data[key].unique_users : 0);
    const getUsers = (key) => (analytics.users[key] ? analytics.users[key].list : []);

    const loading = isFetching || isInitialising || (
      Object.keys(analytics.data).length <= 0 ||
      Object.keys(analytics.users).length <= 0
    );

    return (
      <Container name="Statistics">
        <Container.Content>
          <Bar>
            <h2>
              <Trans i18nKey="network:analytics_flexchange" />
            </h2>
            <div className="pull-right">
              <DateRangeSelector value={selectedDateValue} onChange={this.handleSetDateRange} />
              {!params.networkId && (
                <Select
                  searchable
                  clearable={false}
                  value={selectedNetwork}
                  valueKey="id"
                  labelKey="name"
                  options={[
                    { id: false, name: t('network:analytics_network_selector_all_networks') },
                    ...networks,
                  ]}
                  onChange={this.handleChangeSelectedNetwork}
                />
              )}
            </div>
          </Bar>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans
                  i18nKey="network:analytics_counts_over_period"
                  tOptions={{ context: selectedNetwork ? 'network' : 'organisation' }}
                />
              </Overview.Header.Title>
            </Overview.Header>
            <Overview.Content>
              {loading ? <Spinner size="large" centered /> : (
                <Row>
                  <Row.StatisticsCard
                    color="#FF9500"
                    title={t('network:counts_exchanges_created')}
                    tooltip={t('network:counts_exchanges_created_tooltip')}
                    count={getTotal(EMetricTypes.EXCHANGES_CREATED)}
                    oldCount={getOldTotal(EMetricTypes.EXCHANGES_CREATED)}
                    description={t('network:charts_count_by_unique_users', {
                      count: getUniqueUsers(EMetricTypes.EXCHANGES_CREATED),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#F76844"
                    title={t('network:counts_exchanges_approved')}
                    tooltip={t('network:counts_exchanges_approved_tooltip')}
                    count={getTotal(EMetricTypes.EXCHANGES_APPROVED)}
                    oldCount={getOldTotal(EMetricTypes.EXCHANGES_APPROVED)}
                    description={t('network:charts_count_by_unique_users', {
                      count: getUniqueUsers(EMetricTypes.EXCHANGES_APPROVED),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#FFCC00"
                    title={t('network:counts_exchanges_total_approved_time')}
                    tooltip={t('network:counts_exchanges_total_approved_time_tooltip')}
                    formatter={formatAverageTime}
                    count={
                      getTotal(EMetricTypes.EXCHANGES_TOTAL_APPROVED_TIME_IN_SECONDS) /
                      getTotal(EMetricTypes.EXCHANGES_APPROVED) || 0
                    }
                    oldCount={
                      getOldTotal(EMetricTypes.EXCHANGES_TOTAL_APPROVED_TIME_IN_SECONDS) /
                      getOldTotal(EMetricTypes.EXCHANGES_APPROVED) || 0
                    }
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#4CD964"
                    title={t('network:counts_exchanges_total_time_saved')}
                    tooltip={t('network:counts_exchanges_total_time_saved_tooltip')}
                    formatter={formatAverageTime}
                    count={getTotal(EMetricTypes.EXCHANGES_APPROVED) * 6090}
                    oldCount={getOldTotal(EMetricTypes.EXCHANGES_APPROVED) * 6090}
                    // description={<Trans i18nKey="network:counts_exchanges_total_time_saved_description" components={[<a href="#">a</a>]} />}
                  />
                </Row>
              )}
            </Overview.Content>
          </Overview>
          <Overview.Row>
            <Overview>
              <Overview.Header>
                <Overview.Header.Title>
                  <Trans i18nKey="network:analytics_top_5_shift_creators_in_period" />
                </Overview.Header.Title>
              </Overview.Header>
              {loading ? (
                <Overview.Content>
                  <Spinner size="large" centered />
                </Overview.Content>
              ) : (
                <List
                  items={getUsers(EMetricTypes.EXCHANGES_CREATED)}
                  renderRow={UserCount}
                  placeholder={(
                    <Placeholder
                      title={t('network:analytics_exchanges_created')}
                    />
                  )}
                />
              )}
            </Overview>
            <Overview>
              <Overview.Header>
                <Overview.Header.Title>
                  <Trans i18nKey="network:analytics_top_5_shift_take_overs_in_period" />
                </Overview.Header.Title>
              </Overview.Header>
              {loading ? (
                <Overview.Content>
                  <Spinner size="large" centered />
                </Overview.Content>
              ) : (
                <List
                  items={getUsers(EMetricTypes.EXCHANGES_APPROVED)}
                  renderRow={UserCount}
                  placeholder={(
                    <Placeholder
                      title={t('network:analytics_exchanges_approved')}
                    />
                  )}
                />
              )}
            </Overview>
          </Overview.Row>
        </Container.Content>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  analytics: state.analytics,
  networks: organisationSelector.networks(state),
  permissions: state.permissions,
});

const mapDispatchToProps = {
  fetchAnalytics: require('../../actions').fetchAnalytics,
  fetchAnalyticsUsers: require('../../actions').fetchAnalyticsUsers,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(
    pageWrapper(EEventNames.VISITED_FLEXCHANGE_ANALYTICS_PAGE)(StatisticsFlexchangeContainer)
  )
);
